// File: e1c/src/components/content_editor/NumberBlock.js
// REFACTORED: This version is a fully controlled component, removing internal state
// to resolve input responsiveness issues. It now relies entirely on props from its parent.

import React, { useCallback, useMemo } from 'react';
import { Paper, TextField, IconButton, Grid, Typography, Box } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const NumberBlock = React.memo(({ id, question, options = {}, onChange, onDelete }) => {

    const memoizedOptions = useMemo(() => ({
        unit: options.unit || '',
        min: options.min,
        max: options.max,
        step: options.step ?? 1,
        placeholder: options.placeholder || 'Enter a number',
    }), [options]);


    const handleQuestionChange = useCallback((event) => {
        if (onChange) { onChange({ question: event.target.value }); }
    }, [onChange]);
    
    const handleOptionChange = useCallback((optionName, value) => {
        if (onChange) {
            onChange({ options: { [optionName]: value } });
        }
    }, [onChange]);

    const handleDeleteClick = useCallback(() => {
        if (onDelete) { onDelete(id); }
    }, [id, onDelete]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'relative', '&:hover .delete-button': { opacity: 1 } }}>
            <IconButton
                className="delete-button" aria-label="delete block" onClick={handleDeleteClick} size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 1, color: 'error.main' } }}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>
            
            <TextField
                fullWidth variant="outlined" size="small" label={`Number Input Question Text (ID: ${id})`} multiline
                value={question || ''} onChange={handleQuestionChange}
                placeholder="Enter the question text..." sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth variant="outlined" size="small" label="Unit (Optional)"
                        value={memoizedOptions.unit} onChange={(e) => handleOptionChange('unit', e.target.value)}
                        placeholder="e.g., lbs, steps, times"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth variant="outlined" size="small" label="Placeholder (Optional)"
                        value={memoizedOptions.placeholder} onChange={(e) => handleOptionChange('placeholder', e.target.value)}
                        placeholder="e.g., Enter count"
                    />
                </Grid>
                 <Grid item xs={6} sm={4} md={2}>
                    <TextField
                        type="number" label="Min Value" size="small"
                        value={memoizedOptions.min ?? ''}
                        onChange={(e) => handleOptionChange('min', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                        fullWidth placeholder="Optional" inputProps={{ step: "any" }}
                    />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <TextField
                        type="number" label="Max Value" size="small"
                        value={memoizedOptions.max ?? ''}
                        onChange={(e) => handleOptionChange('max', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                        fullWidth placeholder="Optional" inputProps={{ step: "any" }}
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                    <TextField
                        type="number" label="Step" size="small"
                        value={memoizedOptions.step ?? ''}
                        onChange={(e) => handleOptionChange('step', e.target.value === '' ? 1 : parseFloat(e.target.value))}
                        fullWidth placeholder="e.g., 1 or 0.1" inputProps={{step: "any"}}
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 2, p:1, border: '1px dashed lightgrey', borderRadius: 1 }}>
                <Typography variant="caption" display="block" color="text.secondary" sx={{mb:0.5}}>
                    Patient Preview:
                </Typography>
                <TextField
                    type="number"
                    label={memoizedOptions.unit ? `Value (${memoizedOptions.unit})` : "Number Input"}
                    placeholder={memoizedOptions.placeholder}
                    disabled variant="outlined" size="small"
                    InputProps={{
                        readOnly: true,
                        inputProps: {
                            min: memoizedOptions.min, max: memoizedOptions.max, step: memoizedOptions.step,
                        },
                        endAdornment: memoizedOptions.unit ? <Typography variant="body2" sx={{mr:1}}>{memoizedOptions.unit}</Typography> : null
                    }}
                />
            </Box>
        </Paper>
    );
});

export default NumberBlock;